import { useEffect } from 'react';
import { createAudioInputDeviceTracker, createAudioOutputDeviceTracker } from './webDevices';
import {
  DEVICE_CHANGE_DEBOUNCE_MS,
  NETWORK_ONLINE_POLL_MS,
  RECONNECT_DEBOUNCE_MS,
} from './constants';
import {
  attachSpeakerPermissionListener,
  syncMicrophonePermissionUi,
  syncSpeakerPermissionUi,
  tryMarkSpeakerBlockedFromPlaybackProbe,
  resyncCallAudioToBrowserDefaults,
} from './audioPermissions';
import { clearNetworkReconnectTimeout, markNetworkDrop, markNetworkRecovered } from './networkDrop';
import type { JanusCallRefs } from './sessionRefs';
import { logJanus } from './utils';

export interface JanusWebEffectsParams {
  r: JanusCallRefs;
  isActiveCallSession: () => boolean;
  cancelWebSocketReconnect: () => void;
}

export function useJanusWebEffects({
  r,
  isActiveCallSession,
  cancelWebSocketReconnect,
}: JanusWebEffectsParams): void {
  useEffect(() => {
    let prevNavigatorOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;

    const triggerReconnectFromNetwork = (source: string) => {
      if (!isActiveCallSession()) return;

      if (r.reconnectDebounceTimerRef.current) {
        clearTimeout(r.reconnectDebounceTimerRef.current);
      }
      r.reconnectDebounceTimerRef.current = setTimeout(() => {
        r.reconnectDebounceTimerRef.current = null;
        logJanus('NET', `${source} — schedule full reconnect`);
        markNetworkRecovered(r);
        cancelWebSocketReconnect();
        r.wsReconnectAttemptsRef.current = 0;
        r.scheduleWebSocketReconnectRef.current(true);
      }, RECONNECT_DEBOUNCE_MS);
    };

    const onOffline = () => {
      prevNavigatorOnline = false;
      logJanus('NET', 'offline');
      markNetworkDrop(r, isActiveCallSession, cancelWebSocketReconnect, 'navigator offline');
    };

    const onOnline = () => {
      const wasOffline = !prevNavigatorOnline;
      prevNavigatorOnline = true;
      markNetworkRecovered(r);
      if (wasOffline) {
        triggerReconnectFromNetwork('online event');
      }
    };

    const onLinePollId = window.setInterval(() => {
      const online = navigator.onLine;
      if (prevNavigatorOnline === false && online) {
        markNetworkRecovered(r);
        triggerReconnectFromNetwork('navigator.onLine recovered');
      }
      prevNavigatorOnline = online;
    }, NETWORK_ONLINE_POLL_MS);

    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      window.clearInterval(onLinePollId);
      clearNetworkReconnectTimeout(r);
      if (r.reconnectDebounceTimerRef.current) {
        clearTimeout(r.reconnectDebounceTimerRef.current);
        r.reconnectDebounceTimerRef.current = null;
      }
    };
  }, [cancelWebSocketReconnect, isActiveCallSession, r]);

  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md?.addEventListener) return undefined;

    const outputTracker = createAudioOutputDeviceTracker();
    const inputTracker = createAudioInputDeviceTracker();
    outputTracker.snapshotOutputs();
    inputTracker.snapshotInputs();

    const onDeviceChange = () => {
      if (r.deviceChangeTimerRef.current) clearTimeout(r.deviceChangeTimerRef.current);
      r.deviceChangeTimerRef.current = setTimeout(() => {
        if (!isActiveCallSession()) return;
        if (r.networkDropPendingRef.current) return;
        void (async () => {
          if (navigator.mediaDevices?.enumerateDevices) {
            await navigator.mediaDevices.enumerateDevices();
          }

          const { added: inputAdded, removed: inputRemoved } = await inputTracker.detectInputDeviceChange();
          const { added: outputAdded, removed: outputRemoved } =
            await outputTracker.detectOutputDeviceChange();

          const deviceListChanged =
            inputAdded || inputRemoved || outputAdded || outputRemoved;

          if (
            deviceListChanged ||
            r.micDisconnectHandledRef.current ||
            r.speakerPlaybackWarnedRef.current
          ) {
            logJanus('AUDIO', 'device list changed — resync to browser defaults', {
              inputAdded,
              inputRemoved,
              outputAdded,
              outputRemoved,
            });
            await resyncCallAudioToBrowserDefaults(r, isActiveCallSession);
          }
        })();
      }, DEVICE_CHANGE_DEBOUNCE_MS);
    };

    md.addEventListener('devicechange', onDeviceChange);
    return () => {
      md.removeEventListener('devicechange', onDeviceChange);
      if (r.deviceChangeTimerRef.current) clearTimeout(r.deviceChangeTimerRef.current);
    };
  }, [isActiveCallSession, r]);

  
  useEffect(() => {
    let micPermissionStatus: PermissionStatus | null = null;
    let detachSpeakerPerm: (() => void) | null = null;

    const onMicPermissionChange = () => {
      void syncMicrophonePermissionUi(r, micPermissionStatus, isActiveCallSession);
    };

    const onSpeakerPermissionChange = () => {
      void syncSpeakerPermissionUi(r, isActiveCallSession);
    };

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (!isActiveCallSession()) return;
      void resyncCallAudioToBrowserDefaults(r, isActiveCallSession);
      void syncSpeakerPermissionUi(r, isActiveCallSession);
      void syncMicrophonePermissionUi(r, micPermissionStatus, isActiveCallSession);
      void tryMarkSpeakerBlockedFromPlaybackProbe(r, isActiveCallSession);
    };

    const setup = async () => {
      try {
        micPermissionStatus = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });
        micPermissionStatus.onchange = onMicPermissionChange;
      } catch {
        
      }

      detachSpeakerPerm = await attachSpeakerPermissionListener(onSpeakerPermissionChange);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    void setup();
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (micPermissionStatus) {
        micPermissionStatus.onchange = null;
      }
      detachSpeakerPerm?.();
    };
  }, [isActiveCallSession, r]);
};
